import { Link, useLocation } from "react-router-dom";
import { ChevronDown, LayoutDashboard, LogOut, UserCircle } from "lucide-react";
import silpiLogo from "@/assets/silpi-logo.png";
import { cn } from "@/lib/utils";
import { useLayoutStyle } from "@/hooks/use-layout-style";
import { useAuth } from "@/auth/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { resolvedMenu as menuItems, type ResolvedMenuItem as MenuItem } from "./resolvedMenu";
import { NotificacoesMenu } from "./NotificacoesMenu";

// Réplica visual do header horizontal do AngularJS legado (index.html linhas
// 160-489 + static/css/CabecalhoNovo.css), construída com os primitivos do design
// system novo — não é o CSS/Bootstrap antigo, é uma "pele" que imita o antigo.
// Existe para reduzir resistência à mudança durante a migração (usuário troca de
// visual quando quiser, sem perder nenhuma funcionalidade React por baixo). Tem
// prazo de vida a definir — ver docs/migracao/decisoes.md.
export function LegacyTopBar() {
  const location = useLocation();
  const { setStyle } = useLayoutStyle();
  const { usuario, logout } = useAuth();

  const isActive = (href: string) => (href === "/" ? location.pathname === "/" : location.pathname.startsWith(href));

  const isMenuActive = (item: MenuItem) => {
    if (item.href) return isActive(item.href);
    if (item.subItems) return item.subItems.some((sub) => isActive(sub.href));
    return false;
  };

  const NavItem = ({ item }: { item: MenuItem }) => {
    const active = isMenuActive(item);
    const Icon = item.icon;
    const linkClasses = cn(
      "flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold tracking-wide transition-colors",
      active ? "bg-white text-primary" : "text-white/90 hover:bg-white hover:text-primary",
    );

    if (!item.subItems || item.subItems.length === 0) {
      return (
        <Link to={item.href ?? "#"} className={linkClasses}>
          <Icon className="h-4 w-4" />
          {item.label}
        </Link>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger className={cn(linkClasses, "outline-none")}>
          <Icon className="h-4 w-4" />
          {item.label}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {item.subItems.map((sub) => (
            <DropdownMenuItem key={sub.href} asChild>
              <Link to={sub.href} className={cn("w-full cursor-pointer", isActive(sub.href) && "font-medium text-primary")}>
                {sub.label}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <header className="sticky top-0 z-40 flex h-[50px] items-center justify-between gap-4 bg-primary px-4 shadow-md">
      <Link to="/" className="flex flex-shrink-0 items-center gap-2">
        <img src={silpiLogo} alt="Silpi" className="h-9 w-auto brightness-0 invert" />
      </Link>

      <nav className="flex flex-1 items-center gap-1 overflow-x-auto">
        {menuItems.map((item) => (
          <NavItem key={item.label} item={item} />
        ))}
      </nav>

      <div className="flex flex-shrink-0 items-center gap-1">
        <NotificacoesMenu triggerClassName="text-white/90 hover:bg-white/15 hover:text-white" />

        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-white outline-none transition-colors hover:bg-white/15">
            <UserCircle className="h-5 w-5" />
            <ChevronDown className="h-3.5 w-3.5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <div className="px-3 py-2">
              <p className="truncate text-sm font-medium">{usuario?.nome ?? "—"}</p>
              <p className="truncate text-xs text-muted-foreground">{usuario?.email ?? "—"}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setStyle("sidebar")} className="cursor-pointer">
              <LayoutDashboard className="mr-2 h-4 w-4" />
              Usar o novo menu
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
